import {
  ActionIcon,
  Badge,
  Button,
  Card,
  ColorInput,
  Group,
  Modal,
  Skeleton,
  Stack,
  Table,
  TextInput,
} from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { useDisclosure } from '@mantine/hooks';
import { IconBolt, IconCirclePlus, IconEdit, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

/** Labels CRUD — the "Labels" tab of the Contacts hub. The Auto-labels button
 *  opens the (separate) rule-builder page, which auto-applies these labels. */
export function LabelsPanel() {
  const list = trpc.contacts.labels.list.useQuery();
  const utils = trpc.useUtils();
  const [opened, { open, close }] = useDisclosure(false);
  const [title, setTitle] = useState('');
  const [bg, setBg] = useState('#1971c2');
  const [text, setText] = useState('#ffffff');
  // null = create mode; { labelId } = edit mode. The same modal +
  // local form state serve both.
  const [editing, setEditing] = useState<{ labelId: number } | null>(null);

  const resetForm = () => {
    setTitle('');
    setBg('#1971c2');
    setText('#ffffff');
    setEditing(null);
  };

  const create = trpc.contacts.labels.create.useMutation({
    onSuccess: () => {
      void utils.contacts.labels.list.invalidate();
      close();
      notifications.show({ message: 'Label created', color: 'green' });
      resetForm();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const update = trpc.contacts.labels.update.useMutation({
    onSuccess: () => {
      void utils.contacts.labels.list.invalidate();
      close();
      notifications.show({ message: 'Label updated', color: 'green' });
      resetForm();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const del = trpc.contacts.labels.delete.useMutation({
    onSuccess: () => void utils.contacts.labels.list.invalidate(),
  });

  const startEdit = (l: {
    id: number;
    title: string;
    bgColor: string | null;
    textColor: string | null;
  }) => {
    setEditing({ labelId: l.id });
    setTitle(l.title);
    setBg(l.bgColor ?? '#1971c2');
    setText(l.textColor ?? '#ffffff');
    open();
  };
  const handleClose = () => {
    resetForm();
    close();
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Button
          variant="default"
          leftSection={<IconBolt size={14} />}
          component={Link}
          to="/vendor/contacts/auto-labels"
        >
          Auto-labels
        </Button>
        <Button leftSection={<IconCirclePlus size={14} />} onClick={open}>
          New label
        </Button>
      </Group>
      <Card p={0}>
        {list.isPending ? (
          <Skeleton height={200} m="md" />
        ) : (
          <Table striped highlightOnHover withRowBorders={false}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Preview</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(list.data ?? []).map((l) => (
                <Table.Tr key={l.id}>
                  <Table.Td>
                    <Badge
                      variant="light"
                      style={{
                        backgroundColor: l.bgColor ?? undefined,
                        color: l.textColor ?? undefined,
                      }}
                    >
                      {l.title}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{l.title}</Table.Td>
                  <Table.Td align="right">
                    <Group gap={4} justify="flex-end" wrap="nowrap">
                      <ActionIcon variant="subtle" onClick={() => startEdit(l)} aria-label="Edit">
                        <IconEdit size={14} />
                      </ActionIcon>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => del.mutate({ labelId: l.id })}
                        aria-label="Delete"
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>
      <Modal
        opened={opened}
        onClose={handleClose}
        title={editing ? 'Edit label' : 'New label'}
        centered
      >
        <Stack gap="sm">
          <TextInput
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
          />
          <ColorInput label="Background" value={bg} onChange={setBg} format="hex" />
          <ColorInput label="Text" value={text} onChange={setText} format="hex" />
          <Button
            loading={create.isPending || update.isPending}
            onClick={() => {
              if (editing) {
                update.mutate({ labelId: editing.labelId, title, bgColor: bg, textColor: text });
              } else {
                create.mutate({ title, bgColor: bg, textColor: text });
              }
            }}
          >
            {editing ? 'Save changes' : 'Create'}
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
